import React, { useEffect, useState } from 'react';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import { get, isEmpty } from 'lodash';
import { Image, Carousel, Row } from '@thd-olt-component-react/core-ui';
import PropTypes from 'prop-types';
import PortfolioVideoOverlay from './PortfolioVideoOverlay';
import PortfolioImageOverlay from './PortfolioImageOverlay';
import { resolveWorksuiteImageUrl } from '../../../../../utils/resolveWorksuiteImageUrl';

const PortfolioProject = ({ data }) => {
  const [thumbnails, setThumbnails] = useState({});
  const [videoSlidesReady, setVideoSlidesReady] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState({});
  const [selectedImageSrc, setSelectedImageSrc] = useState(null);
  const [carouselSlides, setCarouselSlides] = useState([]);

  const youtubeImg = ({ id }) => `https://i3.ytimg.com/vi/${id}/hqdefault.jpg`;
  const getVimeoMeta = ({ id }) => {
    return fetch(`https://vimeo.com/api/v2/video/${id}.json`).then((res) => res.json()
    );
  };

  const vimeoImg = (meta) => {
    return get(meta, '0.thumbnail_large');
  };

  const fetchVideoThumbnails = async () => {
    const videos = get(data, 'files.videos');

    if (videos) {
      const thumbs = {};

      // process youtube videos
      videos
        .filter((it) => it.type === 'youtube')
        .forEach((video) => {
          thumbs[video.id] = youtubeImg(video);
        });

      // process vimeo links
      // fetch vimeo video meta info
      const vimeoLinks = await Promise.all(
        videos
          .filter((it) => it.type === 'vimeo')
          .map((video) => {
            return getVimeoMeta(video);
          })
      );

      // take thumbnails
      vimeoLinks.forEach((it) => {
        const id = get(it, '0.id');
        const img = vimeoImg(it);
        thumbs[id] = img;
      });

      setThumbnails({ ...thumbs });
      setVideoSlidesReady(true);
    }
  };

  useEffect(() => {
    fetchVideoThumbnails();
  }, [data]);

  // Depends on videoSlidesReady so that the carousel does not render until both pic and vid slides are ready
  useEffect(() => {

    if (videoSlidesReady) {
      let picSlides = [];
      let vidSlides = data.files.videos.map((it) => renderVideoSlide(it));

      if (!isEmpty(data.files.images)) {
        picSlides = data.files.images.map((it, i) => {
          const imageSrc = resolveWorksuiteImageUrl(it);
          return (
            <div
              key={i}
              className="p2p-portfolio__slider-item"
              onClick={() => setSelectedImageSrc(imageSrc)}
            >
              <Image
                src={imageSrc}
                className="p2p-portfolio__image"
                alt="portfolio project image"
                lazy
              />
            </div>
          );
        });
      }
      setCarouselSlides([].concat(vidSlides, picSlides));
    }
  }, [videoSlidesReady]);

  const handleVideoOverlayClose = () => {
    setSelectedVideo({});
  };

  const handleImageOverlayClose = () => {
    setSelectedImageSrc(null);
  };

  const renderVideoSlide = (video) => (
    <div
      key={video.id}
      className="p2p-portfolio__slider-item"
      onClick={() => {
        setSelectedVideo(video);
      }}
    >
      <PlayCircleOutlineIcon
        style={{ fontSize: 164 }}
        className="p2p-portfolio__play-icon"
      />
      <Image
        src={thumbnails[video.id]}
        className="p2p-portfolio__image"
        alt="portfolio project video thumbnail"
        lazy
      />
    </div>
  );

  if (carouselSlides.length < 1) {
    return null;
  }

  return (
    <>
      <div>
        <p className="pre-formatted">{data.description}</p>
        <div className="p2p-portfolio__project-images">
          <Row className={(carouselSlides.length === 1) ? 'p2p-portfolio__project-one-slide' : ''}>
            <Carousel
              navigation
              dotsBelow
              peek={false}
              slidesPerView="auto"
              slidesPerGroup={1}
              breakpoints={{
                sm: {
                  slidesPerView: 'auto',
                  slidesPerGroup: 1
                },
                md: {
                  slidesPerView: 'auto',
                  slidesPerGroup: 1
                },
                lg: {
                  slidesPerView: 'auto',
                  slidesPerGroup: 1
                },
                xl: {
                  slidesPerView: 'auto',
                  slidesPerGroup: 1
                }
              }}
            >
              {carouselSlides}
            </Carousel>
          </Row>
        </div>
      </div>
      <PortfolioVideoOverlay key={selectedVideo.id} video={selectedVideo} onClose={handleVideoOverlayClose} />
      <PortfolioImageOverlay key={selectedImageSrc} imgSrc={selectedImageSrc} onClose={handleImageOverlayClose} />
    </>
  );
};

PortfolioProject.propTypes = {
  data: PropTypes.shape({
    description: PropTypes.string,
    files: PropTypes.shape({
      images: PropTypes.arrayOf(PropTypes.string),
      videos: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.oneOf(['youtube', 'vimeo', '']),
        id: PropTypes.string
      }))
    }),
    title: PropTypes.string
  }).isRequired
};

export default PortfolioProject;
